/**
 * @reflecto/core — filetransfer/crypto.ts
 *
 * Chunk-level encryption for the file-transfer path ONLY. This is a
 * SEPARATE contract from the messaging path (`../crypto.ts`, tweetnacl,
 * `[0x01 | nonce | ciphertext]` wire format) — do not mix the two.
 *
 * Byte-exact contract: see `crypto-spec.md` in this directory. The frozen
 * interop vectors in `packages/core/test-vectors/filetransfer-v1.json` are
 * the proof; both this module and the Kotlin counterpart must reproduce
 * them exactly.
 *
 * Primitives (crypto-spec.md §2):
 *   - Key derivation: HKDF-SHA-256 (RFC 5869), hand-built from HMAC-SHA-256
 *     because libsodium ships no HKDF primitive.
 *   - Chunk AEAD: XChaCha20-Poly1305-IETF, combined mode (ciphertext ‖ tag).
 *
 * Uses `libsodium-wrappers-sumo` rather than the plain `libsodium-wrappers`
 * build: the plain build's compiled WASM omits HMAC-SHA-256 entirely (no
 * `crypto_auth_hmacsha256*` symbols at all, one-shot or streaming), so it
 * cannot implement RFC 5869 HKDF. The sumo build is the same libsodium.js
 * project at the same version (0.8.4) with the full function set, and its
 * XChaCha20-Poly1305-IETF AEAD output is byte-identical to the plain
 * build's (verified against every frozen vector). `sodium.ready` must be
 * awaited before any call, per libsodium-wrappers' async WASM init.
 */

import sodium from "libsodium-wrappers-sumo";
import { decodeUTF8 } from "tweetnacl-util";
import { AEAD_OVERHEAD, NONCE_PREFIX_LEN } from "./types";

const FT_INFO_PREFIX = decodeUTF8("reflecto-ft-v1"); // 14 ASCII bytes
const HASH_LEN = 32; // HMAC-SHA-256 output length
const HKDF_SALT_LEN = 32; // crypto-spec.md §3 — salt is 32 zero bytes
const TRANSFER_KEY_LEN = 32; // crypto-spec.md §3 — L = 32
const NONCE_LEN = NONCE_PREFIX_LEN + 8; // 24: 16-byte prefix + 8-byte BE counter

/**
 * Thrown by `decryptChunk` ONLY when the AEAD authentication tag fails to
 * verify. Wrong chunk index, wrong final flag, wrong transferId, and
 * ciphertext corruption are all indistinguishable tag failures by design
 * (crypto-spec.md §6) and all surface as this error. Input-shape problems
 * (e.g. a malformed noncePrefix) throw ordinary `Error`/`RangeError`
 * instead, before any AEAD call is made.
 */
export class ChunkAuthError extends Error {
  constructor(message = "chunk failed AEAD authentication") {
    super(message);
    this.name = "ChunkAuthError";
  }
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

async function hmacSha256(key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  const state = sodium.crypto_auth_hmacsha256_init(key);
  sodium.crypto_auth_hmacsha256_update(state, message);
  return sodium.crypto_auth_hmacsha256_final(state);
}

/**
 * HKDF-SHA-256, RFC 5869, built from HMAC-SHA-256 (crypto-spec.md §4).
 * Exported (beyond the module's binding surface) because it's the only
 * seam that can be asserted against the RFC 5869 Appendix A test vectors
 * directly — `deriveTransferKey` only ever exercises it with the fixed
 * file-transfer salt/info shape.
 */
export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number
): Promise<{ prk: Uint8Array; okm: Uint8Array }> {
  const prk = await hmacSha256(salt, ikm);
  const blocks = Math.ceil(length / HASH_LEN);
  if (blocks > 255) {
    throw new RangeError(
      `HKDF: requested length ${length} exceeds the 255 * ${HASH_LEN}-byte maximum`
    );
  }
  let t: Uint8Array = new Uint8Array(0);
  const blockOutputs: Uint8Array[] = [];
  for (let i = 1; i <= blocks; i++) {
    t = await hmacSha256(prk, concatBytes(t, info, new Uint8Array([i])));
    blockOutputs.push(t);
  }
  const okm = concatBytes(...blockOutputs).subarray(0, length);
  return { prk, okm };
}

/**
 * Derive the per-transfer chunk key (crypto-spec.md §3).
 *
 * `sharedSecret` is the raw X25519 `crypto_scalarmult` output — NOT the
 * HSalsa20-hashed `crypto_box_beforenm` key used by the messaging path.
 */
export async function deriveTransferKey(
  sharedSecret: Uint8Array,
  transferId: string,
  senderPub: Uint8Array,
  recipientPub: Uint8Array
): Promise<Uint8Array> {
  const info = concatBytes(FT_INFO_PREFIX, decodeUTF8(transferId), senderPub, recipientPub);
  const salt = new Uint8Array(HKDF_SALT_LEN); // 32 zero bytes
  const { okm } = await hkdfSha256(sharedSecret, salt, info, TRANSFER_KEY_LEN);
  return okm;
}

/**
 * Build the 24-byte per-chunk nonce: `noncePrefix (16 B) ‖ chunkIndex (8 B, big-endian)`
 * (crypto-spec.md §6 nonce layout). Pure and synchronous.
 */
export function chunkNonce(noncePrefix: Uint8Array, chunkIndex: number): Uint8Array {
  if (noncePrefix.length !== NONCE_PREFIX_LEN) {
    throw new RangeError(
      `noncePrefix must be ${NONCE_PREFIX_LEN} bytes, got ${noncePrefix.length}`
    );
  }
  if (!Number.isInteger(chunkIndex) || chunkIndex < 0 || chunkIndex > Number.MAX_SAFE_INTEGER) {
    throw new RangeError(
      `chunkIndex must be a non-negative safe integer no greater than Number.MAX_SAFE_INTEGER, got ${chunkIndex}`
    );
  }
  const nonce = new Uint8Array(NONCE_LEN);
  nonce.set(noncePrefix, 0);
  const view = new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength);
  view.setBigUint64(NONCE_PREFIX_LEN, BigInt(chunkIndex), false); // big-endian
  return nonce;
}

/**
 * Build the chunk AAD: UTF-8 bytes of
 * `${transferId}|${fileId}|${decimal(chunkIndex)}|${isFinal ? "1" : "0"}`
 * (crypto-spec.md §6 AAD layout). Pure and synchronous.
 */
export function chunkAad(
  transferId: string,
  fileId: string,
  chunkIndex: number,
  isFinal: boolean
): Uint8Array {
  const text = `${transferId}|${fileId}|${chunkIndex}|${isFinal ? "1" : "0"}`;
  return decodeUTF8(text);
}

/**
 * Encrypt one file-transfer chunk (crypto-spec.md §6). Combined-mode
 * XChaCha20-Poly1305-IETF — output is `ciphertext ‖ 16-byte tag`.
 */
export async function encryptChunk(
  key: Uint8Array,
  noncePrefix: Uint8Array,
  ids: { transferId: string; fileId: string },
  chunkIndex: number,
  isFinal: boolean,
  plaintext: Uint8Array
): Promise<Uint8Array> {
  await sodium.ready;
  const nonce = chunkNonce(noncePrefix, chunkIndex);
  const aad = chunkAad(ids.transferId, ids.fileId, chunkIndex, isFinal);
  return sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aad, null, nonce, key);
}

/**
 * Decrypt one file-transfer chunk. Throws `ChunkAuthError` if the AEAD
 * tag fails to verify — wrong index, wrong final flag, wrong transferId,
 * and corruption are all indistinguishable tag failures by design
 * (crypto-spec.md §6). Throws `RangeError` for input-shape problems
 * (e.g., wrong key length or too-short ciphertext) before any AEAD call.
 */
export async function decryptChunk(
  key: Uint8Array,
  noncePrefix: Uint8Array,
  ids: { transferId: string; fileId: string },
  chunkIndex: number,
  isFinal: boolean,
  ciphertext: Uint8Array
): Promise<Uint8Array> {
  // Validate input shapes before any AEAD call
  if (key.length !== 32) {
    throw new RangeError(`key must be 32 bytes, got ${key.length}`);
  }
  if (ciphertext.length < AEAD_OVERHEAD) {
    throw new RangeError(
      `ciphertext must be at least ${AEAD_OVERHEAD} bytes (AEAD tag), got ${ciphertext.length}`
    );
  }

  await sodium.ready;
  const nonce = chunkNonce(noncePrefix, chunkIndex);
  const aad = chunkAad(ids.transferId, ids.fileId, chunkIndex, isFinal);
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, aad, nonce, key);
  } catch {
    throw new ChunkAuthError();
  }
}
